import axios from 'axios';
import { API_CONFIG } from '../config/api';

const API_BASE_URL = API_CONFIG.BASE_URL;

// ---------------------------------------------------------------------------
// Token storage helpers (used by both api.ts interceptors and AuthContext)
// ---------------------------------------------------------------------------

const TOKEN_KEYS = {
  access: 'moqr_access_token',
  refresh: 'moqr_refresh_token',
  storage: 'moqr_token_storage', // 'local' | 'session'
};

/** Return the storage backend the user chose (defaults to localStorage). */
function getTokenStorage(): Storage {
  // If the flag was set in sessionStorage, use sessionStorage
  if (sessionStorage.getItem(TOKEN_KEYS.storage) === 'session') {
    return sessionStorage;
  }
  return localStorage;
}

export function storeTokens(
  accessToken: string,
  refreshToken: string,
  rememberMe = true
): void {
  const storage = rememberMe ? localStorage : sessionStorage;
  // Persist which storage we're using so interceptors can read from the right one
  storage.setItem(TOKEN_KEYS.storage, rememberMe ? 'local' : 'session');
  storage.setItem(TOKEN_KEYS.access, accessToken);
  storage.setItem(TOKEN_KEYS.refresh, refreshToken);

  // If switching from one storage to the other, clear the old one
  const other = rememberMe ? sessionStorage : localStorage;
  other.removeItem(TOKEN_KEYS.access);
  other.removeItem(TOKEN_KEYS.refresh);
  other.removeItem(TOKEN_KEYS.storage);
}

export function getStoredAccessToken(): string | null {
  return getTokenStorage().getItem(TOKEN_KEYS.access);
}

export function getStoredRefreshToken(): string | null {
  return getTokenStorage().getItem(TOKEN_KEYS.refresh);
}

export function clearStoredTokens(): void {
  // Clear both storages to be safe
  [localStorage, sessionStorage].forEach((s) => {
    s.removeItem(TOKEN_KEYS.access);
    s.removeItem(TOKEN_KEYS.refresh);
    s.removeItem(TOKEN_KEYS.storage);
  });
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 15000, // 15s so login doesn't hang if backend is unreachable
  headers: {
    'Content-Type': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache',
  },
});

// Also export as default for compatibility
export default api;

// Request interceptor – attach Bearer token from localStorage/sessionStorage
api.interceptors.request.use(
  (config) => {
    const token = getStoredAccessToken();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    // When sending FormData (file uploads), remove the default Content-Type
    // so the browser can set multipart/form-data with the correct boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Only try to refresh token for specific endpoints that require authentication
    const protectedEndpoints = [
      '/profiles/me',
      '/account',
      '/support/tickets',
      '/admin',
      '/superadmin',
      '/subscriptions',
      '/payments'
    ];
    const isProtectedEndpoint = protectedEndpoints.some(endpoint =>
      originalRequest.url?.includes(endpoint)
    );

    if (error.response?.status === 401 && !originalRequest._retry && isProtectedEndpoint) {
      originalRequest._retry = true;

      try {
        // Send stored refresh token in body as fallback when cookies are lost (PWA)
        const storedRefresh = getStoredRefreshToken();
        const refreshResponse = await api.post('/auth/refresh', storedRefresh ? { refresh_token: storedRefresh } : {});

        // Update stored tokens with the new ones from the response
        if (refreshResponse.data?.access_token && refreshResponse.data?.refresh_token) {
          const storage = getTokenStorage();
          const rememberMe = storage === localStorage;
          storeTokens(refreshResponse.data.access_token, refreshResponse.data.refresh_token, rememberMe);
        }

        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed – clear stored tokens
        clearStoredTokens();

        // Only redirect to login if we're on a protected system path
        const pathname = window.location.pathname;
        const segments = pathname.split('/').filter(Boolean);
        const isProtectedSystemPath = ['dashboard', 'admin', 'superadmin'].includes(segments[0]);

        if (isProtectedSystemPath) {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
